import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import APIList, { APIListWrapper } from '../components/APIList'
import DownLoadIcon from '../asset/DownLoadIcon'
import { gql, useQuery } from '@apollo/client'
import { PVLineChart } from '../components/graph/PVLineChart'
import { handleAPIRequest } from '../util/getAPI'
import { createSeriesDataForName } from '../util/dataToLineChart'

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  border-radius: 8px;
  box-shadow: 0px 4px 16px 0px #00000029;
  background-color: #fff;
  overflow: hidden;
`

const Header = styled.div`
  padding: 16px 24px 16px 24px;
  background: #373a40;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  flex-shrink: 0;
`

const Title = styled.div`
  font-family: SUIT;
  font-size: 22px;
  font-weight: 700;
  line-height: 27px;
  letter-spacing: 0em;
  text-align: center;
  color: #ffffff;
`

const BodyWrapper = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  padding: 24px;
  background-color: #fff;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  overflow: hidden;
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  gap: 16px;
  align-items: center;
`

const GraphBox = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  border-radius: 4px;
  gap: 12px;
`

const Bottom = styled.div`
  flex-shrink: 0;
  display: flex;
  gap: 24px;
  justify-content: center;
`

const ButtonBox = styled.div`
  display: flex;
  padding: 8px;
  border-radius: 4px;
  gap: 4px;
  align-items: center;
  cursor: pointer;
`

const ButtonText = styled.div`
  font-family: SUIT;
  font-size: 12px;
  font-weight: 500;
  line-height: 15px;
  letter-spacing: 0em;
  text-align: left;
  color: #4d4d4d;
`

export const findAllPVAPIGQL = gql`
  query findAllPVAPI {
    findAllPVAPI {
      _id
      name
      dt
      hr
      generate_power
    }
  }
`

export const DataPV = () => {
  const { data: OriginPVAPI, loading, error } = useQuery(findAllPVAPIGQL)
  const [PVAPIList, setPVAPIList] = useState<any>()
  const [PVData, setPVData] = useState<any>()
  const [PVHourlyData, setPVHourlyData] = useState<any>()
  const [selected, setSelected] = useState<string | null>(null)

  const formatDayKey = (value: string | Date) => {
    const d = new Date(value)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const formatHourKey = (value: string | Date) => {
    const d = new Date(value)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    const hour = String(d.getHours()).padStart(2, '0')
    return `${year}-${month}-${day} ${hour}:00`
  }

  useEffect(() => {
    if (selected) {
      const rawData: [Date, number][] = createSeriesDataForName(
        OriginPVAPI,
        selected,
        'generate_power'
      )

      type AggregatedData = { [key: string]: number }
      const dailyAggregatedData = rawData.reduce(
        (acc: AggregatedData, [date, value]: [Date, number]) => {
          const formattedDate = formatDayKey(date)
          if (!acc[formattedDate]) {
            acc[formattedDate] = 0
          }
          acc[formattedDate] += value
          return acc
        },
        {} as AggregatedData
      )

      const hourlyAggregatedData = rawData.reduce(
        (acc: AggregatedData, [date, value]: [Date, number]) => {
          const formattedDate = formatHourKey(date)
          if (!acc[formattedDate]) {
            acc[formattedDate] = 0
          }
          acc[formattedDate] += value
          return acc
        },
        {} as AggregatedData
      )

      const formattedDailyData: any = Object.entries(dailyAggregatedData)
        .map(([date, value]) => [date, value])
        .sort(([a], [b]) => String(a).localeCompare(String(b)))

      const formattedHourlyData: any = Object.entries(hourlyAggregatedData)
        .map(([date, value]) => [date, value])
        .sort(([a], [b]) => String(a).localeCompare(String(b)))

      setPVData(formattedDailyData)
      setPVHourlyData(formattedHourlyData)
    }
  }, [selected, OriginPVAPI])

  useEffect(() => {
    if (OriginPVAPI) {
      const uniqueData = OriginPVAPI?.findAllPVAPI.reduce(
        (acc: any, current: any) => {
          const x = acc.find((item: any) => item.name === current.name)
          if (!x) {
            return acc.concat([current])
          } else {
            return acc
          }
        },
        []
      )

      setPVAPIList(uniqueData)
    }
  }, [OriginPVAPI])

  if (loading) return <p>Loading...</p>
  if (error) return <p>Error : {error.message}</p>

  console.log('PVData :', PVData)

  const handleDownload = () => {
    if (!selected || !PVHourlyData || PVHourlyData.length === 0) return

    const escapeCsv = (value: string | number | null | undefined) =>
      `"${String(value ?? '').replace(/"/g, '""')}"`

    const header = '날짜,데이터'
    const rows = PVHourlyData.map((item: [string, number]) =>
      [escapeCsv(item[0]), escapeCsv(item[1])].join(',')
    )
    const csvContent = [header, ...rows].join('\n')

    const blob = new Blob(['\ufeff', csvContent], {
      type: 'text/csv;charset=utf-8;',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `pv-${selected}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return (
    <>
      <APIListWrapper>
        <APIList
          dataList={PVAPIList?.map((v: any) => v.name)}
          setSelected={setSelected}
          apiFunc={() =>
            handleAPIRequest(`${process.env.REACT_APP_BACKEND_URL}/pv`)
          }
          noFile
          noApi
        />
      </APIListWrapper>
      <ContentWrapper>
        <Header>
          <Title>PV 발전량</Title>
        </Header>
        <BodyWrapper>
          {selected && (
            <Body>
              <GraphBox>
                <PVLineChart
                  title="PV 발전량"
                  data={PVData}
                  width="100%"
                  height="100%"
                />
              </GraphBox>
              <Bottom>
                <ButtonBox onClick={handleDownload}>
                  <DownLoadIcon />
                  <ButtonText>다운로드</ButtonText>
                </ButtonBox>
                {/* <ButtonBox>
                  <DeleteIcon />
                  <ButtonText>삭제</ButtonText>
                </ButtonBox> */}
              </Bottom>
            </Body>
          )}
        </BodyWrapper>
      </ContentWrapper>
    </>
  )
}
